import React, {useEffect, useState} from "react";
import {
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Form,
    Grid,
    Input,
    InputNumber,
    message,
    Modal,
    Popconfirm,
    Row,
    Spin,
    Tag,
    Typography,
    Carousel
} from "antd";
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    ShoppingOutlined
} from "@ant-design/icons";
import Layout from "../Layout";

const {Title, Text} = Typography;
const {useBreakpoint} = Grid;

/** A small overlay component to show a spinner on top of the product card
 *  when an operation (e.g. delete) is in progress.
 */
const SpinOverlay = ({text}) => (
    <div
        style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1
        }}
    >
        <Spin tip={text}/>
    </div>
);

const ProductsPage = () => {
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState({
        page: true,
        modal: false,
        deleting: null
    });

    const screens = useBreakpoint();
    const accessToken = localStorage.getItem("accessToken");

    useEffect(() => {
        fetchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    const fetchProducts = async () => {
        try {
            setLoading((prev) => ({...prev, page: true}));
            const response = await fetch(
                "https://api.21stcenturyav.info/woocommerce/products",
                {
                    headers: {Authorization: `Bearer ${accessToken}`}
                }
            );
            if (!response.ok) throw new Error("Failed to fetch products");
            const data = await response.json();
            setProducts(data.products || []);
        } catch (error) {
            console.error("Error fetching products:", error);
            message.error("Error fetching products");
        } finally {
            setLoading((prev) => ({...prev, page: false}));
        }
    };

    const handleViewDetails = async (productId) => {
        try {
            setLoading((prev) => ({...prev, modal: true}));
            const response = await fetch(
                `https://api.21stcenturyav.info/woocommerce/products/${productId}`,
                {
                    headers: {Authorization: `Bearer ${accessToken}`}
                }
            );
            if (!response.ok) throw new Error("Failed to fetch product details");
            const product_data = await response.json();
            setSelectedProduct(product_data.product);
            setIsModalVisible(true);
            form.setFieldsValue(product_data.product);
        } catch (error) {
            console.error("Error fetching product:", error);
            message.error("Error loading product details");
        } finally {
            setLoading((prev) => ({...prev, modal: false}));
        }
    };

    const handleUpdate = async (values) => {
        if (!selectedProduct) return;
        try {
            setLoading((prev) => ({...prev, modal: true}));
            const response = await fetch(
                `https://api.21stcenturyav.info/woocommerce/products/${selectedProduct.id}`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(values)
                }
            );

            if (!response.ok) throw new Error("Failed to update product");
            message.success("Product updated successfully");
            setIsEditing(false);
            fetchProducts();
        } catch (error) {
            console.error("Error updating product:", error);
            message.error("Error updating product");
        } finally {
            setLoading((prev) => ({...prev, modal: false}));
        }
    };

    const handleDelete = async (productId) => {
        try {
            setLoading((prev) => ({...prev, deleting: productId}));
            const response = await fetch(
                `https://api.21stcenturyav.info/woocommerce/products/${productId}`,
                {
                    method: "DELETE",
                    headers: {Authorization: `Bearer ${accessToken}`}
                }
            );

            if (!response.ok) throw new Error("Failed to delete product");
            message.success("Product deleted successfully");
            setIsModalVisible(false);
            fetchProducts();
        } catch (error) {
            console.error("Error deleting product:", error);
            message.error("Error deleting product");
        } finally {
            setLoading((prev) => ({...prev, deleting: null}));
        }
    };

    // Action buttons for the bottom of each product card
    const cardActions = (product) => [
        <Button
            key="view"
            type="text"
            icon={<EyeOutlined/>}
            onClick={() => handleViewDetails(product.id)}
            disabled={!!loading.deleting}
        />,
        <Button
            key="edit"
            type="text"
            icon={<EditOutlined/>}
            onClick={() => {
                handleViewDetails(product.id);
                setIsEditing(true);
            }}
            disabled={!!loading.deleting}
        />,
        <Popconfirm
            key="delete"
            title="Delete this product?"
            onConfirm={() => handleDelete(product.id)}
            okText="Delete"
            cancelText="Cancel"
            placement="topRight"
        >
            <Button
                type="text"
                danger
                icon={<DeleteOutlined/>}
                loading={loading.deleting === product.id}
            />
        </Popconfirm>
    ];

    return (
        <Layout pageTitle="Products">
            <div
                style={{
                    maxWidth: 1440,
                    margin: "0 auto",
                    padding: screens.xs ? "16px" : "24px"
                }}
            >
                {/* Page Header */}
                <div
                    style={{
                        textAlign: "center",
                        marginBottom: 48,
                        padding: "32px 0",
                        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                        borderRadius: 8
                    }}
                >
                    <Title level={2} style={{margin: 0, fontWeight: 600}}>
                        <ShoppingOutlined style={{marginRight: 12, color: "#1890ff"}}/>
                        Product Catalog
                    </Title>
                    <Text type="secondary" style={{fontSize: 16}}>
                        Manage your product inventory with ease
                    </Text>
                </div>

                {/* Main Content */}
                {loading.page ? (
                    <div style={{textAlign: "center", margin: "100px 0"}}>
                        <Spin size="large"/>
                    </div>
                ) : products.length === 0 ? (
                    <Empty description="No products found" style={{margin: "80px 0"}}>
                        <Button type="primary">Add New Product</Button>
                    </Empty>
                ) : (
                    <Row gutter={[24, 24]}>
                        {products.map((product) => (
                            <Col
                                key={product.id}
                                xs={24}
                                sm={12}
                                lg={8}
                                xl={6}
                                style={{display: "flex"}}
                            >
                                <Card
                                    hoverable
                                    style={{
                                        width: "100%",
                                        borderRadius: 8,
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
                                    }}
                                    // ----- IMAGE / CAROUSEL COVER -----
                                    cover={
                                        <div style={{
                                            position: "relative", height: 200,  // Set a fixed height for uniformity
                                            overflow: "hidden",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "#f8f9fa",
                                        }}>
                                            {product.images?.length ? (
                                                <Carousel
                                                    dotPosition="bottom"
                                                    draggable
                                                    autoplay={product.images.length > 1} // Autoplay only if multiple images
                                                    style={{height: "100"}}
                                                >
                                                    {product.images.map((img, index) => (
                                                        <div
                                                            key={img.id || index}
                                                            style={{
                                                                height: 200,
                                                                width: "100%",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center"
                                                            }}
                                                        >
                                                            <img
                                                                alt={product.name}
                                                                src={img.src}
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    objectFit: "cover",
                                                                    borderTopLeftRadius: 8,
                                                                    borderTopRightRadius: 8
                                                                }}
                                                            />
                                                        </div>
                                                    ))}
                                                </Carousel>
                                            ) : (
                                                <div
                                                    style={{
                                                        height: 240,
                                                        background: "#f8f9fa",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        borderTopLeftRadius: 8,
                                                        borderTopRightRadius: 8
                                                    }}
                                                >
                                                    <Text type="secondary">No Image</Text>
                                                </div>
                                            )}
                                            {loading.deleting === product.id && (
                                                <SpinOverlay text="Deleting..."/>
                                            )}
                                        </div>
                                    }
                                    actions={cardActions(product)}
                                >
                                    <Card.Meta
                                        title={<Text strong ellipsis>{product.name}</Text>}
                                        description={
                                            <>
                                                {/* Short description */}
                                                <Text
                                                    type="secondary"
                                                    ellipsis={{rows: 2}}
                                                    style={{
                                                        display: "block",
                                                        minHeight: 44,
                                                        margin: "8px 0"
                                                    }}
                                                >
                          <span
                              dangerouslySetInnerHTML={{
                                  __html: product.short_description
                              }}
                          />
                                                </Text>

                                                {/* Stock & Price row */}
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        marginTop: 12
                                                    }}
                                                >
                                                    <Tag color="blue" style={{margin: 0}}>
                                                        {product.stock_quantity} in stock
                                                    </Tag>
                                                    <Text
                                                        strong
                                                        style={{fontSize: 16, color: "#1890ff"}}
                                                    >
                                                        £{product.price}
                                                    </Text>
                                                </div>
                                            </>
                                        }
                                    />
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}

                {/* Product Details & Edit Modal */}
                <Modal
                    title={
                        <div style={{display: "flex", alignItems: "center", gap: 8}}>
                            {isEditing ? <EditOutlined/> : <EyeOutlined/>}
                            {isEditing ? "Edit Product" : "Product Details"}
                        </div>
                    }
                    visible={isModalVisible}
                    onCancel={() => {
                        setIsModalVisible(false);
                        setIsEditing(false);
                    }}
                    footer={null}
                    width={screens.xs ? "95%" : 800}
                    destroyOnClose
                >
                    {loading.modal ? (
                        <div style={{textAlign: "center", padding: "40px 0"}}>
                            <Spin size="large"/>
                        </div>
                    ) : isEditing ? (
                        /* ---------- EDIT FORM ---------- */
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleUpdate}
                            initialValues={selectedProduct}
                        >
                            <Form.Item label="Product Name" name="name">
                                <Input/>
                            </Form.Item>
                            <Form.Item label="Description" name="description">
                                <Input.TextArea rows={4}/>
                            </Form.Item>
                            <Form.Item label="Short Description" name="short_description">
                                <Input.TextArea rows={2}/>
                            </Form.Item>
                            <Form.Item label="Price" name="price">
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    style={{width: "100%"}}
                                    formatter={(value) => `£${value}`}
                                />
                            </Form.Item>
                            <Form.Item label="SKU" name="sku">
                                <Input/>
                            </Form.Item>
                            <Form.Item label="Stock Quantity" name="stock_quantity">
                                <InputNumber min={0} style={{width: "100%"}}/>
                            </Form.Item>
                            <div style={{display: "flex", gap: 10, marginTop: 20}}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading.modal}
                                >
                                    Save Changes
                                </Button>
                                <Button
                                    onClick={() => {
                                        setIsEditing(false);
                                    }}
                                >
                                    Cancel
                                </Button>
                            </div>
                        </Form>
                    ) : (
                        /* ---------- VIEW DETAILS ---------- */
                        selectedProduct && (
                            <>
                                {/* If multiple images, show them all in a carousel at the top */}
                                <div
                                    style={{
                                        marginBottom: 24,
                                        borderRadius: 6,
                                        overflow: "hidden"
                                    }}
                                >
                                    {selectedProduct.images?.length ? (
                                        <Carousel
                                            dotPosition="bottom"
                                            draggable
                                            autoplay={selectedProduct.images.length > 1}
                                            style={{maxHeight: 400}}
                                        >
                                            {selectedProduct.images.map((img, idx) => (
                                                <div
                                                    key={img.id || idx}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        height: 400,
                                                        background: "#f8f9fa"
                                                    }}
                                                >
                                                    <img
                                                        alt={selectedProduct.name}
                                                        src={img.src}
                                                        style={{
                                                            maxHeight: "100%",
                                                            maxWidth: "100%",
                                                            objectFit: "cover"
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </Carousel>
                                    ) : (
                                        <div
                                            style={{
                                                height: 240,
                                                background: "#f8f9fa",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center"
                                            }}
                                        >
                                            <Text type="secondary">No Image</Text>
                                        </div>
                                    )}
                                </div>

                                <Descriptions bordered column={1}>
                                    <Descriptions.Item label="Product Name">
                                        {selectedProduct.name}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Description">
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: selectedProduct.description
                                            }}
                                        />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Short Description">
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: selectedProduct.short_description
                                            }}
                                        />
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Price">
                    <span
                        style={{
                            color: "#1890ff",
                            fontWeight: 500
                        }}
                    >
                      £{selectedProduct.price}
                    </span>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="SKU">
                                        <Tag color="blue">{selectedProduct.sku}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Stock Quantity">
                                        <Tag
                                            color={
                                                selectedProduct.stock_quantity > 0 ? "green" : "red"
                                            }
                                        >
                                            {selectedProduct.stock_quantity}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Categories">
                                        {selectedProduct.categories?.map((category) => (
                                            <Tag key={category.id} color="geekblue">
                                                {category.name}
                                            </Tag>
                                        ))}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Tags">
                                        {selectedProduct.tags?.map((tag) => (
                                            <Tag key={tag.id} color="purple">
                                                {tag.name}
                                            </Tag>
                                        ))}
                                    </Descriptions.Item>
                                </Descriptions>

                                <div style={{display: "flex", gap: 10, marginTop: 20}}>
                                    <Button
                                        type="primary"
                                        onClick={() => setIsEditing(true)}
                                        loading={loading.modal}
                                    >
                                        Edit Product
                                    </Button>
                                    <Button onClick={() => setIsModalVisible(false)}>Close</Button>
                                </div>
                            </>
                        )
                    )}
                </Modal>
            </div>
        </Layout>
    );
};

export default ProductsPage;
